import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import moment from 'moment';

import { toSentenceCase } from '../../../utils';

import {
  DATE_TIME_FORMAT,
  DEFAULT_END_ANY_TIME,
  DEFAULT_END_TIME,
  DEFAULT_START_TIME,
  errorMessages,
} from '../constants';

// initial values

const getIsSameTimeRange = (schedule) => {
  const [sample, ...otherDays] = Object.values(schedule);
  return !otherDays.find((day) => !isEqual(sample, day));
};

export const getIsAnyTime = (startTime, endTime) => {
  if (!startTime || !endTime) return false;
  return (
    startTime.slice(0, 8) === DEFAULT_START_TIME &&
    endTime.slice(0, 8) === DEFAULT_END_ANY_TIME
  );
};

const isInitialAnyTime = (schedule) =>
  Object.values(schedule)
    ?.flat()
    ?.map(({ startTime, endTime }) => getIsAnyTime(startTime, endTime))
    ?.filter((isAnyTime) => isAnyTime === false)?.length === 0;

const formatSchedule = (rawSchedule) => {
  const schedule = {};
  Object.keys(rawSchedule).forEach((key) => {
    schedule[key] = rawSchedule[key].length > 0 ? rawSchedule[key] : [{}];
  });
  return schedule;
};

export const getInitialSchedule = (rawSchedule) => {
  const isSameTimeRange = getIsSameTimeRange(rawSchedule);
  const isEveryDayOn =
    isSameTimeRange && Object.values(rawSchedule).length === 7;
  const everyday = {
    isOn: isEveryDayOn,
    timeSlots:
      isEveryDayOn && isInitialAnyTime(rawSchedule)
        ? [{ startTime: DEFAULT_START_TIME, endTime: DEFAULT_END_ANY_TIME }]
        : isEveryDayOn
        ? Object.values(rawSchedule)?.[0]
        : [{}],
  };

  const schedule = isEveryDayOn
    ? { everyday }
    : {
        everyday,
        ...formatSchedule(rawSchedule),
      };
  return schedule;
};

export const getTimePickerId = (name, index, isStartTime) =>
  `timePicker${name}${index}${isStartTime ? 'start' : 'end'}`;

export const parseLimitTime = (time) => time?.split(':');

export const formatTimeRange = (time, format = DATE_TIME_FORMAT) => {
  if (!time) return null;
  const [h, m, s] = parseLimitTime(time);
  return moment().set({ h, m, s }).format(format);
};

export const getMomentDateAndTime = (date) =>
  moment(date).format(DATE_TIME_FORMAT).split(' ');

// helpers functions
export function generatePossibleHours() {
  const hours = [];
  for (let i = 0; i < 24; i += 1) {
    hours.push(i);
  }
  return hours;
}

export function generatePossibleMinutes() {
  const minutes = [];
  for (let i = 0; i < 60; i += 1) {
    minutes.push(i);
  }
  return minutes;
}

export function setMomentTime(rawTime) {
  if (!rawTime) return null;
  let time = rawTime;
  if (
    (typeof time === 'string' && moment(time).isValid()) ||
    (typeof time === 'object' && moment.isMoment(time))
  ) {
    const [, parsedTime] = getMomentDateAndTime(time);
    time = parsedTime;
  }
  const [h, m, s] = parseLimitTime(time);
  return moment().set({ h, m, s }).format();
}

const formatTime = (time) => time && `${moment().format('YYYY-MM-DD')} ${time}`;

const getAllDaySelectionError = (dayNames) =>
  `Note - the time slot(s) for ${dayNames.join(
    ', ',
  )} cover the whole day - please modify or select 'Any Time'`;

const getEndBeforeStartError = (dayNames) =>
  `End time cannot be before start time - ${dayNames.join(', ')}`;

// schedule validation
export const getDisabledScheduleValues = (
  type,
  fieldName,
  startTimeValue,
  endTimeValue,
  selectedHour,
) => {
  const startTime = setMomentTime(startTimeValue);
  const endTime = setMomentTime(endTimeValue);
  const possibleTimeValues =
    type === 'hour' ? generatePossibleHours() : generatePossibleMinutes();
  let disabledTimeValues = [];

  possibleTimeValues.forEach((value) => {
    const timeOnUI =
      type === 'hour'
        ? moment().set({ h: value, m: 59 })
        : moment().set({
            h: selectedHour,
            m: value,
          });
    if (
      fieldName === 'startTime' &&
      moment(timeOnUI).isAfter(moment(endTime), type)
    ) {
      disabledTimeValues.push(value);
    }
    if (
      fieldName === 'endTime' &&
      moment(timeOnUI).isBefore(moment(startTime), type)
    ) {
      disabledTimeValues.push(value);
    }
  });

  if (fieldName === 'endTime' && (type === 'hour' || selectedHour === 0)) {
    disabledTimeValues = [...new Set(disabledTimeValues)].filter(
      (value) => value !== 0,
    );
  }

  return [...new Set(disabledTimeValues)];
};

export const getIsTimeError = (day) => {
  const { startTime, endTime: end } = day;
  const endTime = setMomentTime(end);
  const endHour = moment(endTime).hour();
  const endMinute = moment(endTime).minute();
  const disabledHours = getDisabledScheduleValues(
    'hour',
    'endTime',
    startTime,
    endTime,
  );
  const disabledMinutes = getDisabledScheduleValues(
    'minute',
    'endTime',
    startTime,
    endTime,
    endHour,
  );
  const isInvalidHour = disabledHours?.find(
    (disabledHour) => disabledHour === endHour,
  );
  const isInvalidMinute = disabledMinutes?.find(
    (disabledMinute) => disabledMinute === endMinute,
  );
  return isInvalidHour || isInvalidMinute;
};

export const scheduleValidation = (value) => {
  const { everyday, ...weekDays } = value || {};
  const errors = {}; // are passed to the redux form
  const commonErrors = []; // list of errors <str> - later are set as errors.schedule

  const days = { ...weekDays };
  // everyday validation - if on -> validate in the same way as week days
  if (everyday && everyday?.isOn) {
    days.everyday = everyday.timeSlots;
  }

  // check if schedule is empty
  if (!everyday?.isOn) {
    const nonEmptyDays = Object.values(days)
      .flat()
      .filter((timeSlot) => !isEmpty(timeSlot));
    if (nonEmptyDays.length < 1) {
      commonErrors.push(errorMessages.emptyScheduleError);
    }
  }

  // validate time for each day
  if (days && Object.values(days)?.length > 0) {
    const invalidEndAfterStartDaysNames = [];
    const invalidAllDaySlotsDaysNames = [];

    Object.keys(days).forEach((dayName) => {
      const validTimeSlots = [];

      // validate each time slot of a day
      days[dayName].forEach((timeSlot, i) => {
        // time slot ids are used to highlight with red invalid fields
        const startTimeSlotId = getTimePickerId(dayName, i, true);
        const endTimeSlotId = getTimePickerId(dayName, i, false);

        // check if end time is after start time
        const isInvalidTimeSlot = getIsTimeError(timeSlot);
        if (isInvalidTimeSlot) {
          invalidEndAfterStartDaysNames.push(toSentenceCase(dayName));
          errors[endTimeSlotId] = errorMessages.invalidTimeError;
          return null;
        }

        // validate all time slots against the current one
        if (!validTimeSlots.length) {
          // if it's the first time slot - it's a valid one
          validTimeSlots.push(timeSlot);
        } else {
          let isValidTimeSlot = true;

          validTimeSlots.forEach(({ startTime, endTime }) => {
            const { startTime: currentStart, endTime: currentEnd } = timeSlot;

            // validate current time slot against others valid
            if (!currentStart || !currentEnd) return null;

            // add date value to get a moment valid string
            const startInt = formatTime(startTime);
            const endInt = formatTime(endTime);
            const startCurrent = formatTime(currentStart);
            const endCurrent = formatTime(currentEnd);

            // duplicates are not valid
            if (currentStart === startTime && currentEnd === endTime) {
              isValidTimeSlot = false;
              commonErrors.push(errorMessages.alreadyExists);
              return null;
            }

            // start and end time shouldn't be between any other time slot
            if (
              moment(startCurrent).isBetween(startInt, endInt, 'minute', '()')
            ) {
              isValidTimeSlot = false;
              commonErrors.push(errorMessages.timeOverlapError);
              return null;
            }
            if (
              moment(endCurrent).isBetween(startInt, endInt, 'minute', '()')
            ) {
              isValidTimeSlot = false;
              commonErrors.push(errorMessages.timeOverlapError);
              return null;
            }

            // start and end time should be both before or after of any selected time slot
            if (
              !(
                (moment(startCurrent).isSameOrBefore(startInt, 'minute') &&
                  moment(endCurrent).isSameOrBefore(startInt, 'minute')) ||
                (moment(startCurrent).isSameOrAfter(endInt, 'minute') &&
                  moment(endCurrent).isSameOrAfter(endInt, 'minute'))
              )
            ) {
              isValidTimeSlot = false;
              commonErrors.push(errorMessages.timeOverlapError);
            }
            return null;
          });

          // if it's a valid time slot - add to others valid
          // if not - set time slot errors for a specific id to highlight the invalid field with red
          if (isValidTimeSlot) {
            validTimeSlots.push(timeSlot);
          } else {
            errors[startTimeSlotId] = errorMessages.invalidTimeIntervalError;
            errors[endTimeSlotId] = errorMessages.invalidTimeIntervalError;
          }
        }
        return null;
      });

      // check if time slots cover all 24 hours
      // check if any time slot has start time equals to the beginning of the day
      const hasDefaultStartTime = validTimeSlots.find(
        ({ startTime }) => startTime === DEFAULT_START_TIME,
      );

      // check if any time slot has end time equals to the end of the day
      const hasDefaultEndTime = validTimeSlots.find(
        ({ endTime }) => endTime === DEFAULT_END_TIME,
      );

      // if time slots cover the beginning and the end of the day
      // check if any free time left for this day
      // if there is no free time -> any time should be selected
      if (hasDefaultStartTime && hasDefaultEndTime) {
        const startTimesWithoutDefaultStart = validTimeSlots
          .filter(({ startTime }) => startTime !== DEFAULT_START_TIME)
          .map(({ startTime }) => startTime);

        const endTimesWithoutDefaultEnd = validTimeSlots
          .filter(({ endTime }) => endTime !== DEFAULT_END_TIME)
          .map(({ endTime }) => endTime);

        const hasFreeTime = startTimesWithoutDefaultStart.find(
          (startTime) =>
            !endTimesWithoutDefaultEnd.find((endTime) => startTime === endTime),
        );

        !hasFreeTime &&
          invalidAllDaySlotsDaysNames.push(toSentenceCase(dayName));
      }
    });
    if (invalidEndAfterStartDaysNames.length) {
      commonErrors.push(getEndBeforeStartError(invalidEndAfterStartDaysNames));
    }
    if (invalidAllDaySlotsDaysNames.length) {
      commonErrors.push(getAllDaySelectionError(invalidAllDaySlotsDaysNames));
    }
  }
  if (commonErrors.length) {
    errors.schedule = [...new Set(commonErrors)].join('. ');
  }
  return errors;
};
